<template>
  <ion-card color="medium" class="single-podcast">
    <EditButton v-if="canEdit" :url="`streams/${podcastData.id}/edit`" />
    <ion-grid class="single-podcast-main">
      <ion-row>
        <ion-col class="img-col">
          <AppImg
            :img-src="banner"
            img-alt="banner"
            border-radius="10px"
            class="img-wrap"
          />
        </ion-col>

        <ion-col
          v-if="!isDesktopScreen"
          class="is-flex ion-align-items-center ion-margin-start"
        >
          <p class="fw-500 fz-14 title is-displaying-two-lines-text mb-2">
            {{
              podcastData.title ||
              podcastData.stream_title ||
              podcastData.course_title
            }}
          </p>
        </ion-col>

        <ion-col
          size="12"
          size-lg="8"
          class="is-flex is-flex-direction-column ion-justify-content-center"
          :class="{ 'ion-margin-start': isDesktopScreen }"
        >
          <p
            v-if="isDesktopScreen"
            class="fw-500 fz-14 title is-displaying-two-lines-text mb-2"
          >
            {{
              podcastData.title ||
              podcastData.stream_title ||
              podcastData.course_title
            }}
          </p>

          <div class="fz-12 fw-300 subtitle">
            <!-- Description -->
            <p
              class="fz-12 fw-300 mb-2 is-displaying-two-lines-text description"
              v-html="description"
            ></p>

            <!-- Date -->
            <span>{{ podcastDate || streamDate }}</span>

            <!-- Duration -->
            <span v-if="duration" class="bullet">&bull;</span>

            <!-- Minutes Left -->
            <span v-if="playbackProgressPercentage && minutesLeft > 0">
              {{ minutesLeft }} {{ $t('podcast.minLeft') }}
            </span>

            <span v-else-if="duration">
              {{ Math.round(duration / 60) }}
              min</span
            >

            <!-- Progress -->
            <AppProgress
              v-if="playbackProgressPercentage && minutesLeft > 0"
              class="progress"
              :progress="playbackProgressPercentage"
              has-dynamic-colors
            />

            <p
              v-else-if="playbackProgressPercentage && minutesLeft === 0"
              class="completed"
            >
              <span v-if="duration" class="bullet">&bull;</span>
              {{ $t('streams.completed') }}
              <IonIcon :icon="checkmarkCircle" color="primary" class="ml-1" />
            </p>
          </div>
        </ion-col>
      </ion-row>
    </ion-grid>

    <div class="single-podcast-footer is-flex">
      <div class="actions is-flex ion-align-items-center">
        <ShareButton
          class="mr-2"
          :text="podcastData.title"
          :series-id="seriesId"
          :course-id="
            podcastData.streams?.[0]?.course_id || podcastData.course_id
          "
          :stream-id="podcastData.streams?.[0]?.id || podcastData.id"
        />
        <FavouriteButton
          :course-id="
            podcastData.streams?.[0]?.course_id || podcastData.course_id
          "
          :stream-id="podcastData.streams?.[0]?.id || podcastData.id"
          @update:favorites="$emit('update:favorites')"
        />
      </div>

      <PlayerReproduction
        class="player-reproduction"
        :duration="podcastData.duration"
        :current-time="currentTime"
        :hideProgress="true"
      />
    </div>
  </ion-card>
</template>

<script setup>
import PlayerReproduction from '@/components/common/PlayerReproduction.vue';
import AppImg from '@/components/common/AppImg.vue';
import ShareButton from '@/components/common/ShareButton.vue';
import FavouriteButton from '@/components/common/FavouriteButton.vue';
import EditButton from '@/components/common/EditButton.vue';
import AppProgress from '@/components/common/AppProgress';
import { checkmarkCircle } from 'ionicons/icons';

import { useI18n } from 'vue-i18n';

import {
  CATEGORY_GENERAL_MEDIA,
  CATEGORY_BANNER_MEDIA,
  CATEGORY_STREAM_STILL,
  CATEGORY_PODCAST_IMG,
} from '@/config/constants';

const PRIOR_IMAGES = [
  CATEGORY_PODCAST_IMG,
  CATEGORY_STREAM_STILL,
  CATEGORY_BANNER_MEDIA,
  CATEGORY_GENERAL_MEDIA,
];

import useDeviceScreen from '@/composables/useDeviceScreen';

import { IonIcon, IonCard, IonGrid, IonCol, IonRow } from '@ionic/vue';

import { computed } from '@vue/reactivity';
import {
  getLatestTrackPositionByTimestamp,
  getSecondsFromHMSString,
  getSmallestSizeImage,
} from '@/helpers/functions';
import { useStore } from 'vuex';

defineOptions({
  name: 'SinglePodcast',
});

const props = defineProps({
  podcastData: {
    type: Object,
    required: true,
    default() {
      return {};
    },
  },
  courseMedia: {
    type: Array,
    default: () => [],
  },
  seriesId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:favorites']);

const { isDesktopScreen } = useDeviceScreen();
const { t } = useI18n();
const options = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'Europe/Amsterdam',
};
const store = useStore();

const description = computed(() => {
  const rawHtml =
    props.podcastData.description ??
    props.podcastData.course_introduction ??
    '';

  // Remove <a> tags that do not include the allowed domain, but keep inner text
  return rawHtml.replace(
    /<a\s+[^>]*href="(?!https:\/\/medischescholing\.nl)[^"]*"[^>]*>(.*?)<\/a>/gi,
    '$1',
  );
});

const canEdit = computed(() => {
  const hasPermission =
    store.getters['authentication/hasPermission']('update_stream');
  const isWeb = store.state.app.platform === 'web';
  return hasPermission && isWeb;
});

const podcastDate = computed(() => {
  const { rss_feed_publish_date, published_at, created_at } = props.podcastData;

  const publishDate = rss_feed_publish_date ?? published_at ?? created_at;
  if (!publishDate) return '';

  const date = new Date(publishDate).toLocaleDateString(
    t('podcast.local'),
    options,
  );

  return date === 'Invalid Date' ? '' : date;
});

const streamDate = computed(() => {
  const date = new Date(
    props.podcastData.streams[0]?.updated_at ||
      props.podcastData.streams[0]?.created_at,
  ).toLocaleDateString(t('podcast.local'), options);
  return date !== 'Invalid Date' ? date : '';
});

const banner = computed(() => {
  const validStreamImages =
    props.podcastData.media?.filter((media) =>
      PRIOR_IMAGES.includes(media.category),
    ) || [];
  validStreamImages.sort((a, b) => b.category - a.category);
  const streamImg = getSmallestSizeImage(validStreamImages)?.url ?? null;
  const courseImg = getSmallestSizeImage(props.courseMedia)?.url ?? null;
  return streamImg || courseImg;
});

const currentTime = computed(() => {
  const propData = props.podcastData.stream_user?.[0] ?? props.podcastData;
  const storageData =
    store.state.player.recentlyPlayedProgress[props.podcastData.id];
  const time = getLatestTrackPositionByTimestamp(propData, storageData);
  if (time) {
    return getSecondsFromHMSString(time);
  }
  return null;
});

const duration = computed(() => {
  return props.podcastData.duration || props.podcastData.streams?.[0]?.duration;
});

const playbackProgressPercentage = computed(() => {
  const time = currentTime.value;

  const hasValidTime = time !== null && time !== undefined;
  const hasValidDuration =
    duration.value !== null && duration.value !== undefined;

  if (!hasValidTime || !hasValidDuration) return null;

  const progressRatio = time / duration.value;
  return isNaN(progressRatio) ? 0 : progressRatio * 100;
});

const minutesLeft = computed(() => {
  const secondsLeft = duration.value - currentTime.value;
  return Math.round(secondsLeft / 60);
});
</script>

<style lang="scss" scoped>
@use '@/theme/custom' as *;

.single-podcast {
  margin-left: auto;
  margin-right: auto;
  padding: 10px;
  margin: 0;

  @media (max-width: $tablet-breakpoint) {
    padding: 5px 10px;
  }

  &-main {
    margin-bottom: 10px;

    .img-wrap {
      height: 100px;
      width: 100px;

      :deep(img) {
        width: 100%;
        height: 100%;
      }
    }

    .subtitle {
      margin: 10px 0;

      .description {
        * {
          color: white !important;
          font-size: 12px;
        }
      }
    }

    .progress {
      width: 60px;
      display: inline-block;
      margin: 0 10px;

      &:deep(.track__progress-container--wrapper) {
        height: 2px;
        background-color: white;
        margin-bottom: 2px;
      }
    }

    .completed {
      display: inline-flex;
      align-items: center;
    }

    @media (max-width: $tablet-breakpoint) {
      .subtitle {
        margin-bottom: 6px;
        font-size: 10px;
      }
    }
  }

  &-footer {
    justify-content: space-between;

    > :first-child {
      margin-right: 40px;
    }

    .player-reproduction {
      width: auto;

      &:deep(.play-circle-wrap) {
        width: fit-content;
      }
    }

    @media (max-width: $tablet-breakpoint) {
      .actions {
        &:deep(ion-icon) {
          font-size: 20px;
        }
      }
    }
  }
}

.bullet {
  padding: 0 10px;
}

ion-grid {
  --ion-grid-padding: 0;
  --ion-grid-column-padding: 0;
}

.img-col {
  flex-basis: 90px;
  flex-grow: unset;
  height: 100%;
}

@media (max-width: $tablet-breakpoint) {
  .img-col {
    flex-basis: 50px;
  }
}
</style>
